import { apiRequest } from './api';

// ── Types ────────────────────────────────────────────

export type InvoiceStatus = 'paid' | 'partial' | 'overdue' | 'published' | 'draft' | 'void';

export type InvoiceApiRecord = {
    id: string;
    organization_id: string;
    student_id: string;
    academic_session_id: string;
    invoice_number: string;
    title: string;
    type: string;
    status: InvoiceStatus;
    total_amount: string;
    paid_amount: string;
    balance_amount: string;
    issue_date: string;
    due_date: string | null;
    created_at: string;
    updated_at: string;
    student: {
        id: string;
        name: string | null;
        enrollment_number: string | null;
    } | null;
    academic_session: {
        id: string;
        name: string;
    };
    // The list endpoint now embeds line items on each invoice.
    items?: InvoiceDetailItem[];
};

export type InvoiceListMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

export type InvoiceListResponse = {
    data: InvoiceApiRecord[];
    meta: InvoiceListMeta;
};

export type InvoiceDetailItem = {
    id: string;
    invoice_id: string;
    fee_head_id: string;
    description: string | null;
    gross_amount: string;
    applied_discount_id: string | null;
    discount_amount: string;
    net_amount: string;
    is_optional: boolean;
    created_at: string;
    fee_head: { id: string; name: string; type: string };
    applied_discount: { id: string; name: string } | null;
};

export type InvoiceDetailRecord = InvoiceApiRecord & {
    items: InvoiceDetailItem[];
};

// ── API Functions ─────────────────────────────────────

export async function fetchInvoiceById(id: string): Promise<InvoiceDetailRecord> {
    const res = await apiRequest<InvoiceDetailRecord>(`/api/student-invoices/${id}`);
    return res.data;
}

export async function fetchInvoiceItems(invoiceId: string): Promise<InvoiceDetailItem[]> {
    const res = await apiRequest<InvoiceDetailItem[]>(`/api/student-invoices/${invoiceId}/items`);
    return res.data;
}

export async function deleteInvoiceItem(itemId: string): Promise<void> {
    await apiRequest(`/api/items/${itemId}`, { method: 'DELETE' });
}

export async function publishInvoice(id: string): Promise<void> {
    await apiRequest(`/api/student-invoices/${id}/publish`, { method: 'POST' });
}

export async function fetchInvoices(params?: {
    student_id?: string;
    academic_session_id?: string;
    status?: InvoiceStatus | '';
    per_page?: number;
    page?: number;
}): Promise<InvoiceListResponse> {
    const query = new URLSearchParams();
    if (params?.student_id) query.set('student_id', params.student_id);
    if (params?.academic_session_id) query.set('academic_session_id', params.academic_session_id);
    if (params?.status) query.set('status', params.status);
    if (params?.per_page) query.set('per_page', String(params.per_page));
    if (params?.page) query.set('page', String(params.page));
    const qs = query.toString();

    const res = await apiRequest<InvoiceApiRecord[]>(
        `/api/student-invoices${qs ? `?${qs}` : ''}`
    );

    return {
        data: res.data,
        meta: (res as unknown as { meta: InvoiceListMeta }).meta,
    };
}
